import { LinkedAccount, Invitation } from '@/types/residents/home';
import { BaseAPI } from '../../shared/BaseAPI';
import { InvitationStatus } from '@/constants/residents/invitationStatus';
import { Lease } from '@/types/residents/lease';
import { UnitInfoResponse, WifiInfo } from '@/types/residents/wifi';
import { Bulletin } from '@/types/residents/bulletin';
import { Event } from '@/types/residents/event';
import { CommunityResource } from '@/types/residents/communityResource';
import { RoommatesResponse } from '@/types/residents/roommates';

class HomeAPIService extends BaseAPI {
  private static homeInstance: HomeAPIService;

  static getInstance(): HomeAPIService {
    if (!HomeAPIService.homeInstance) {
      HomeAPIService.homeInstance = new HomeAPIService();
    }
    return HomeAPIService.homeInstance;
  }

  async getEvents(): Promise<Event[]> {
    // Match iOS: Endpoint(domain: .resio(.v1), path: "users/events")
    const response = await this.get<{ success?: boolean; response?: any[] }>('/api/v1/users/events');
    const list = Array.isArray(response?.response) ? response.response : [];
    // Map backend fields to RN Event type and normalize
    const mapped = list
      .filter((e) => e && (e.id !== undefined && e.id !== null))
      .map((e) => {
        const photos = Array.isArray(e.photos)
          ? e.photos
              .filter((p: any) => p && (p.id !== undefined && p.id !== null))
              .map((p: any) => ({ id: p.id, photoUrl: p.photoUrl, label: p.label }))
          : [];

        const nRsvps =
          (typeof e.nRsvps === 'number' ? e.nRsvps : undefined) ??
          (typeof e.n_rsvps === 'number' ? e.n_rsvps : undefined) ??
          (Array.isArray(e.rsvpList) ? e.rsvpList.length : 0);

        const nPhotos =
          (typeof e.nPhotos === 'number' ? e.nPhotos : undefined) ??
          (typeof e.n_photos === 'number' ? e.n_photos : undefined) ??
          photos.length;

        return {
          id: e.id,
          propertyId: e.propertyId,
          name: e.name,
          location: e.location,
          address: e.address,
          startTime: e.startTime,
          startTimeLocal: e.startTimeLocal,
          startTimeLocalFormatted: e.startTimeLocalFormatted,
          endTime: e.endTime,
          endTimeLocal: e.endTimeLocal,
          endTimeLocalFormatted: e.endTimeLocalFormatted,
          createdAt: e.createdAt,
          updatedAt: e.updatedAt,
          description: e.description,
          // Backend uses descriptionHtml; keep legacy alias descriptionHTML for UI compatibility
          descriptionHTML: e.descriptionHTML || e.descriptionHtml,
          responseDescription: e.responseDescription,
          rsvp: !!e.rsvp,
          rsvpLimit: typeof e.rsvpLimit === 'number' ? e.rsvpLimit : 0,
          nRsvps,
          nPhotos,
          photos,
        } as Event;
      });
    return mapped;
  }

  async setEventRSVP(eventId: number, rsvp: boolean): Promise<void> {
    // Match iOS: Endpoint(domain: .resio(.v1), path: "users/events/rsvp")
    const resp = await this.patch<{ success?: boolean; message?: string }>(
      '/api/v1/users/events/rsvp',
      {
        // Backend validator expects String for eventId
        eventId: String(eventId),
        rsvp,
      }
    );
    if (resp && resp.success === false) {
      // Surface backend message (e.g., event is full) as an error
      throw new Error(resp.message || 'Failed to update RSVP');
    }
  }

  async getBulletins(propertyId?: number, pageNum: number = 0): Promise<Bulletin[]> {
    // Validate inputs to prevent injection
    if (propertyId !== undefined && (!Number.isInteger(propertyId) || propertyId < 0)) {
      throw new Error('Invalid propertyId parameter');
    }
    if (!Number.isInteger(pageNum) || pageNum < 0) {
      throw new Error('Invalid pageNum parameter');
    }

    // Use URLSearchParams for safe URL construction
    const baseUrl = '/api/v2/bulletins';
    let url = baseUrl;
    
    if (propertyId) {
      const params = new URLSearchParams({
        propertyId: propertyId.toString(),
        pageNum: pageNum.toString()
      });
      url = `${baseUrl}?${params.toString()}`;
    }
    
    try {
      const response = await this.get<{ success: boolean; data: Bulletin[] }>(url);
      return response.data || [];
    } catch (error) {
      // Re-throw with sanitized error message for security
      throw new Error('Failed to fetch bulletins');
    }
  }

  /**
   * Fetch user notifications (web parity)
   * Mirrors resio-web: GET /api/v1/users/bulletins
   */
  async getNotifications(): Promise<Bulletin[]> {
    try {
      const resp: any = await this.get<any>(`/api/v1/users/bulletins`);
      const list = resp?.response ?? resp?.data ?? [];
      if (!Array.isArray(list)) return [];
      // Map web notification shape to Bulletin used by RN UI
      const mapped: Bulletin[] = list.map((n: any) => ({
        id: n?.id,
        title: n?.subject ?? n?.title,
        description: n?.description,
        descriptionHtml: n?.descriptionHtml ?? n?.descriptionHTML,
        body: (n?.descriptionHtml ?? n?.descriptionHTML ?? n?.description) || '',
        createdAt: n?.createdAt ?? n?.created_at ?? new Date().toISOString(),
        messageReadCount: n?.read ? '1' : '0',
        author: n?.author
          ? {
              id: n.author.id,
              firstName: n.author.firstName,
              lastName: n.author.lastName,
              email: n.author.email,
            }
          : undefined,
        files: Array.isArray(n?.files)
          ? n.files.map((f: any) => ({
              id: f.id,
              fileUrl: f.fileUrl,
              label: f.label,
            }))
          : [],
      }));
      return mapped;
    } catch (e) {
      return [];
    }
  }

  async getBulletin(bulletinId: number): Promise<Bulletin> {
    const response = await this.get<{ response: Bulletin }>(`/api/v2/bulletins/${bulletinId}`);
    return response.response;
  }

  async markBulletinAsRead(bulletinId: number): Promise<void> {
    // Mirror resio-web: PATCH /api/v1/users/read-bulletin with body { bulletinId }
    await this.patch(`/api/v1/users/read-bulletin`, { bulletinId });
  }

  async deleteBulletin(bulletinId: number): Promise<void> {
    // Mirror resio-web: DELETE /api/v1/users/delete-bulletin with body { bulletinId }
    await this.delete(`/api/v1/users/delete-bulletin`, { data: { bulletinId } as any });
  }

  async getUnreadBulletinsCount(propertyId: number): Promise<number> {
    // Match iOS endpoint exactly: /v2/bulletins/unread?propertyId=X
    const response = await this.get<{ success: boolean; count: number }>(`/api/v2/bulletins/unread?propertyId=${propertyId}`);
    return response.count || 0;
  }

  async getCommunityResources(propertyId?: number): Promise<CommunityResource[]> {
    // Match iOS: Endpoint(domain: .resio(.v1), path: "resources?propertyId=...")
    const baseUrl = '/api/v1/resources';
    let url = baseUrl;
    if (propertyId !== undefined) {
      const params = new URLSearchParams({ propertyId: String(propertyId) });
      url = `${baseUrl}?${params.toString()}`;
    }
    const response = await this.get<{ response: CommunityResource[] }>(url);
    return response.response || [];
  }

  async getLeases(): Promise<Lease[]> {
    const response = await this.get<{ response: Lease[] }>('/api/v2/leases');
    return response.response || [];
  }

  async getCurrentLease(): Promise<Lease | null> {
    const response = await this.get<{ response: Lease }>('/api/v2/leases/current');
    return response.response || null;
  }

  // getConnectedAccounts removed; use getLinkedAccounts() which calls /api/v2/users/connections

  /**
   * Fetch roommates for the current user
   * Matches iOS: Endpoint(domain: .resio(.v1), path: "users/me/roommates")
   */
  async getRoommates(): Promise<RoommatesResponse> {
    const response = await this.get<{ success: boolean; response: RoommatesResponse }>(`/api/v1/users/me/roommates`);
    return response.response;
  }

  /**
   * Get linked accounts for the current user
   * Matches iOS: Endpoint(domain: .resio(.v2), path: "users/connections")
   */
  async getLinkedAccounts(): Promise<LinkedAccount[]> {
    try {
      // Match iOS endpoint exactly: /api/v2/users/connections
      const response = await this.get<{ success: boolean; response: LinkedAccount[] }>('/api/v2/users/connections');
      return response.response || [];
    } catch (error) {
      console.error('🔗 HomeAPI.getLinkedAccounts - API Error:', error);
      // Re-throw with sanitized error message for security
      throw new Error('Failed to fetch linked accounts');
    }
  }

  /**
   * Delete a linked account connection
   * Matches iOS: Endpoint.Connections.deleteConnection(with: String(id), status: status)
   */
  async deleteLinkedAccount(accountId: number): Promise<void> {
    // Validate inputs to prevent injection
    if (!Number.isInteger(accountId) || accountId <= 0) {
      throw new Error('Invalid account ID parameter');
    }

    try {      
      // Match iOS exactly: use invitations endpoint with intent=CANCELLED status
      // iOS enum Status: cancelled = 5
      await this.delete(`/api/v2/users/invitations/${accountId}?intent=${InvitationStatus.CANCELLED}`);
    } catch (error) {
      console.error('🔗 HomeAPI.deleteLinkedAccount - API Error:', error);
      // Re-throw with sanitized error message for security
      throw new Error('Failed to delete linked account');
    }
  }

  /**
   * Get invitations for the current user
   * Matches iOS: Endpoint(domain: .resio(.v2), path: "users/invitations")
   */
  async getInvitations(): Promise<Invitation[]> {
    try {
      // Match iOS endpoint exactly: /api/v2/users/invitations
      const response = await this.get<{ success: boolean; response: Invitation[] }>('/api/v2/users/invitations');
      return response.response || [];
    } catch (error) {
      console.error('📨 HomeAPI.getInvitations - API Error:', error);
      // Re-throw with sanitized error message for security
      throw new Error('Failed to fetch invitations');
    }
  }

  /**
   * Accept an invitation
   * Matches iOS: api.putInvitation(id: invite.id, status: .accepted)
   */
  async acceptInvitation(invitationId: number): Promise<void> {
    // Validate inputs to prevent injection
    if (!Number.isInteger(invitationId) || invitationId <= 0) {
      throw new Error('Invalid invitation ID parameter');
    }

    try {
      // Match iOS: PUT /api/v2/users/invitations/{id} with status: ACCEPTED
      await this.put(`/api/v2/users/invitations/${invitationId}`, { status: InvitationStatus.ACCEPTED });
    } catch (error) {
      console.error('📨 HomeAPI.acceptInvitation - API Error:', error);
      // Re-throw with sanitized error message for security
      throw new Error('Failed to accept invitation');
    }
  }

  /**
   * Decline/Cancel an invitation
   * Matches iOS: api.deleteConnection(id: invite.id, status: invite.isSender ? .cancelled : .declined)
   */
  async declineInvitation(invitationId: number, sender: boolean): Promise<void> {
    // Validate inputs to prevent injection
    if (!Number.isInteger(invitationId) || invitationId <= 0) {
      throw new Error('Invalid invitation ID parameter');
    }

    try {
      // Match iOS logic: status = sender ? .cancelled : .declined
      const status = sender ? InvitationStatus.CANCELLED : InvitationStatus.DECLINED;
      await this.delete(`/api/v2/users/invitations/${invitationId}?intent=${status}`);
    } catch (error) {
      console.error('📨 HomeAPI.declineInvitation - API Error:', error);
      // Re-throw with sanitized error message for security
      throw new Error('Failed to decline invitation');
    }
  }

  /**
   * Resend an invitation
   * Mirrors web behavior: resendInvitation(inviteId)
   */
  async resendInvitation(invitationId: number): Promise<void> {
    if (!Number.isInteger(invitationId) || invitationId <= 0) {
      throw new Error('Invalid invitation ID parameter');
    }

    try {
      // Backend expects: PUT /api/v2/users/invitations/resend/{invitationId}
      await this.put(`/api/v2/users/invitations/resend/${invitationId}`);
    } catch (error) {
      console.error('📨 HomeAPI.resendInvitation - API Error:', error);
      throw new Error('Failed to resend invitation');
    }
  }

  /**
   * Send a new invitation
   * Matches iOS: api.postInvitation(message: message, email: email)
   */
  async sendInvitation(email: string, message: string): Promise<void> {
    // Basic validation
    if (!email || !email.includes('@')) {
      throw new Error('Invalid email address');
    }
    if (!message || message.trim().length === 0) {
      throw new Error('Message cannot be empty');
    }

    try {
      // Match iOS endpoint: POST /api/v2/users/invitations
      await this.post('/api/v2/users/invitations', { email, message });
    } catch (error) {
      console.error('📨 HomeAPI.sendInvitation - API Error:', error);
      
      // Handle specific error cases like iOS does
      // BaseAPI formatError creates: { code: "409", message: "...", details?: ... }
      if (error && typeof error === 'object') {
        const apiError = error as any;
        const statusCode = apiError.code; // "409", "500", etc.
        const errorMessage = apiError.message;
                
        // For 409 (conflict), show a user-friendly duplicate invitation message
        if (statusCode === '409' || statusCode === 409) {
          throw new Error('This person has already been invited');
        }
        
        // For other errors, show the server message if available  
        if (errorMessage && !errorMessage.includes('Request failed with status code')) {
          throw new Error(errorMessage);
        }
      }
      
      // Fallback for unknown errors
      throw new Error('Failed to send invitation');
    }
  }

  async getPropertyInfo(propertyId: number): Promise<any> {
    const response = await this.get<{ response: any }>(`/api/v2/properties/${propertyId}`);
    return response.response;
  }

  /**
   * Get unit WiFi information for a lease
   * Matches iOS: Endpoint(domain: .resio(.v2), path: "users/unit-information/\(leaseID)")
   */
  async getUnitInfo(leaseId: number): Promise<UnitInfoResponse> {
    // Validate inputs to prevent injection
    if (!Number.isInteger(leaseId) || leaseId <= 0) {
      throw new Error('Invalid leaseId parameter');
    }

    try {
      // Match iOS endpoint exactly: /api/v2/users/unit-information/{leaseId}
      const response = await this.get<{ success: boolean; response: UnitInfoResponse }>(
        `/api/v2/users/unit-information/${leaseId}`
      );
      return response.response || {};
    } catch (error) {
      // Handle 404 errors quietly; other errors log and rethrow.
      // The backend can surface "not enabled" via a DoesNotExistError (HTTP 404,
      // envelope code "does-not-exist") — e.g. when cbx-wifi-integration-enabled
      // is off for the property. BaseAPI.formatError currently flattens this to
      // { code: "404" }, but we check multiple fields so this stays robust if
      // the error shape changes.
      if (error && typeof error === 'object') {
        const apiError = error as any;
        const code = apiError.code;
        const status = apiError.status ?? apiError.statusCode;
        if (
          code === '404' ||
          code === 404 ||
          code === 'does-not-exist' ||
          status === 404
        ) {
          return {};
        }
      }
      console.error('📶 HomeAPI.getUnitInfo - API Error:', error);
      throw new Error('Failed to fetch unit WiFi information');
    }
  }

  /**
   * Get WiFi information directly for current user's active lease
   * Note: This method requires a valid lease ID to be passed from the calling code
   */
  async getCurrentWifiInfo(leaseId?: number): Promise<WifiInfo | null> {
    if (!leaseId) {
      throw new Error('No active lease ID available for WiFi information');
    }
    
    try {
      const unitInfo = await this.getUnitInfo(leaseId);
      // Handle backend's hyphenated key structure
      return unitInfo['unit-info']?.wifi || unitInfo.unitInfo?.wifi || null;
    } catch (error) {
      throw new Error('Failed to fetch current WiFi information');
    }
  }
}

export const HomeAPI = HomeAPIService.getInstance(); 